import { msalInstance } from "services/msalInstance";
import { buildAcquireTokenSilentRequest } from "services/authProvider";

/** @type {Promise<string> | null} */
let refreshInFlight = null;

/**
 * Resolves the MSAL account used for silent token acquisition.
 * @returns {import("@azure/msal-browser").AccountInfo | null}
 */
function resolveAccount() {
  return (
    msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null
  );
}

/**
 * Acquires a fresh ID token from MSAL (refresh token / cache) and persists it
 * to `localStorage` under `msal.idtoken` for the axios bearer interceptor.
 *
 * @returns {Promise<string>} New ID token (JWT).
 * @throws If no account or silent acquisition fails.
 */
export async function refreshMsalIdTokenAndPersist() {
  const account = resolveAccount();
  if (!account) {
    throw new Error("No MSAL account for token refresh");
  }
  const result = await msalInstance.acquireTokenSilent(
    buildAcquireTokenSilentRequest(account),
  );
  if (!result?.idToken) {
    throw new Error("Silent token acquisition returned no id token");
  }
  localStorage.setItem("msal.idtoken", result.idToken);
  return result.idToken;
}

/**
 * Same as {@link refreshMsalIdTokenAndPersist} but coalesces concurrent callers
 * (e.g. many parallel API requests after idle) into a single MSAL round-trip.
 *
 * @returns {Promise<string>}
 */
export function refreshMsalIdTokenCoalesced() {
  if (!refreshInFlight) {
    refreshInFlight = refreshMsalIdTokenAndPersist().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}
